import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  QrCode, 
  Users, 
  Clock, 
  Eye, 
  Phone, 
  Globe, 
  MapPin, 
  Navigation,
  TrendingUp,
  Star,
  Smartphone,
  Monitor,
  BarChart3,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const API_URL = '';
const LOGO_WORDMARK = "https://customer-assets.emergentagent.com/job_qr-navigator/artifacts/vkpvwgue_logo%20%281%29%20%281%29.png";

const TIME_FILTERS = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: 'Last 7 Days' },
  { id: '30d', label: 'Last 30 Days' },
  { id: 'all', label: 'All Time' }
];

const CHART_COLORS = {
  primary: '#14B8A6',
  secondary: '#0EA5E9',
  tertiary: '#8B5CF6',
  quaternary: '#F59E0B'
};

// Format seconds to readable duration
const formatDuration = (seconds) => {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
};

// Metric Card Component
const MetricCard = ({ icon: Icon, label, value, subtext, color = 'teal' }) => (
  <div className="bg-white rounded-2xl p-5 shadow-sm shadow-black/5" data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 rounded-xl bg-${color}-50 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}-600`} />
      </div>
    </div>
    <p className="text-2xl font-bold text-slate-900 mb-1">{value}</p>
    <p className="text-sm font-medium text-slate-600">{label}</p>
    {subtext && <p className="text-xs text-slate-400 mt-1">{subtext}</p>}
  </div>
);

// Conversion Metric Card
const ConversionCard = ({ icon: Icon, label, value, percentage }) => (
  <div className="bg-white rounded-2xl p-4 shadow-sm shadow-black/5 flex items-center gap-4">
    <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center flex-shrink-0">
      <Icon className="w-6 h-6 text-teal-600" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xl font-bold text-slate-900">{value.toLocaleString()}</p>
      <p className="text-sm text-slate-500">{label}</p>
    </div>
    {percentage !== undefined && (
      <div className="text-right">
        <span className="text-sm font-medium text-teal-600">{percentage}%</span>
        <p className="text-xs text-slate-400">of views</p>
      </div>
    )}
  </div>
);

// Top Listing Row
const TopListingRow = ({ rank, name, category, views, interactions, isFeatured }) => (
  <div className="flex items-center gap-3 py-3 border-b border-slate-100 last:border-0">
    <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600">
      {rank}
    </span>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <p className="text-sm font-medium text-slate-900 truncate">{name}</p>
        {isFeatured && <Star className="w-3 h-3 text-amber-500 fill-amber-500" />}
      </div>
      <p className="text-xs text-slate-400 capitalize">{category}</p>
    </div>
    <div className="text-right">
      <p className="text-sm font-semibold text-slate-900">{views.toLocaleString()}</p>
      <p className="text-xs text-slate-400">{interactions} actions</p>
    </div>
  </div>
);

// Category Bar
const CategoryBar = ({ category, engagement, maxEngagement }) => {
  const width = Math.max((engagement / maxEngagement) * 100, 5);
  return (
    <div className="mb-3 last:mb-0">
      <div className="flex justify-between text-sm mb-1">
        <span className="font-medium text-slate-700 capitalize">{category}</span>
        <span className="text-slate-500">{engagement.toLocaleString()}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div 
          className="h-full bg-teal-500 rounded-full transition-all duration-500"
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
};

export default function AdminAnalyticsPage() {
  const [period, setPeriod] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/analytics?period=${period}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
        setError(null);
      } else {
        setError('Failed to load analytics');
      }
    } catch (err) {
      console.error('Analytics fetch error:', err);
      setError('Unable to connect');
    } finally {
      setLoading(false);
    }
  };

  // Calculate conversion percentages
  const totalViews = data?.engagement?.listings_viewed || 1;
  const callRate = data ? Math.round((data.conversions.call_clicks / totalViews) * 100) : 0;
  const websiteRate = data ? Math.round((data.conversions.website_clicks / totalViews) * 100) : 0;
  const mapRate = data ? Math.round((data.conversions.map_opens / totalViews) * 100) : 0;
  const directionRate = data ? Math.round((data.conversions.direction_requests / totalViews) * 100) : 0;

  const maxCategoryEngagement = data?.top_categories?.[0]?.engagement || 1;

  return (
    <div className="min-h-screen bg-[#F2F2F7]" data-testid="admin-analytics-page">
      {/* Header */}
      <header className="sticky top-0 z-40 glass-header h-14 px-5">
        <div className="max-w-5xl mx-auto h-full flex items-center gap-4">
          <Link 
            to="/admin/businesses"
            className="w-9 h-9 rounded-xl bg-slate-100/80 flex items-center justify-center nav-icon-glow"
          >
            <ArrowLeft className="w-4 h-4 text-slate-700" />
          </Link>
          <div className="flex-1">
            <h1 className="text-base font-semibold text-slate-900">Creator Analytics</h1>
            <p className="text-xs text-slate-500">Platform Performance</p>
          </div>
          <img src={LOGO_WORDMARK} alt="Disqover" className="h-5" />
        </div>
      </header>

      {/* Time Filter */}
      <div className="px-5 py-4 border-b border-slate-200/50 bg-white/60 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {TIME_FILTERS.map((filter) => (
            <Button
              key={filter.id}
              variant={period === filter.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPeriod(filter.id)}
              className={`flex-shrink-0 rounded-full px-4 ${
                period === filter.id 
                  ? 'bg-teal-600 hover:bg-teal-700 text-white' 
                  : 'bg-white hover:bg-slate-50'
              }`}
              data-testid={`filter-${filter.id}`}
            >
              {filter.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <main className="px-5 py-6">
        <div className="max-w-5xl mx-auto space-y-6">
          
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-teal-600 animate-spin mb-3" />
              <p className="text-sm text-slate-500">Loading analytics...</p>
            </div>
          ) : error ? (
            <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
              <BarChart3 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 mb-4">{error}</p>
              <Button onClick={fetchAnalytics} className="bg-teal-600 hover:bg-teal-700">
                Try Again
              </Button>
            </div>
          ) : (
            <>
              {/* Summary Metrics */}
              <section>
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
                  Platform Overview
                </h2>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <MetricCard 
                    icon={QrCode}
                    label="QR Scans"
                    value={data.summary.qr_scans.toLocaleString()}
                    subtext="Total scans"
                    color="teal"
                  />
                  <MetricCard 
                    icon={Users}
                    label="Unique Users"
                    value={data.summary.unique_users.toLocaleString()}
                    subtext="Distinct visitors"
                    color="blue"
                  />
                  <MetricCard 
                    icon={TrendingUp}
                    label="Total Sessions"
                    value={data.summary.total_sessions.toLocaleString()}
                    subtext="App sessions"
                    color="purple"
                  />
                  <MetricCard 
                    icon={Clock}
                    label="Avg Duration"
                    value={formatDuration(data.summary.avg_session_duration)}
                    subtext="Per session"
                    color="amber"
                  />
                </div>
              </section>

              {/* Engagement Metrics */}
              <section>
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
                  Engagement
                </h2>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white rounded-2xl p-4 shadow-sm text-center">
                    <Eye className="w-6 h-6 text-teal-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-slate-900">{data.engagement.listings_viewed.toLocaleString()}</p>
                    <p className="text-xs text-slate-500">Listings Viewed</p>
                  </div>
                  <div className="bg-white rounded-2xl p-4 shadow-sm text-center">
                    <BarChart3 className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-slate-900">{data.engagement.pages_per_session}</p>
                    <p className="text-xs text-slate-500">Pages/Session</p>
                  </div>
                  <div className="bg-white rounded-2xl p-4 shadow-sm text-center">
                    <TrendingUp className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-slate-900">{data.engagement.avg_interactions}</p>
                    <p className="text-xs text-slate-500">Avg Interactions</p>
                  </div>
                </div>
              </section>

              {/* Conversion Metrics - MOST IMPORTANT */}
              <section>
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
                  Conversions
                  <span className="ml-2 text-xs font-normal text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">Key Metrics</span>
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <ConversionCard 
                    icon={Phone}
                    label="Call Clicks"
                    value={data.conversions.call_clicks}
                    percentage={callRate}
                  />
                  <ConversionCard 
                    icon={Globe}
                    label="Website Clicks"
                    value={data.conversions.website_clicks}
                    percentage={websiteRate}
                  />
                  <ConversionCard 
                    icon={MapPin}
                    label="Map Opens"
                    value={data.conversions.map_opens}
                    percentage={mapRate}
                  />
                  <ConversionCard 
                    icon={Navigation}
                    label="Direction Requests"
                    value={data.conversions.direction_requests}
                    percentage={directionRate}
                  />
                </div>
              </section>

              {/* Daily Chart */}
              <section>
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
                  Activity Over Time
                </h2>
                <div className="bg-white rounded-2xl p-5 shadow-sm">
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.chart_data} barGap={4}>
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 10, fill: '#94a3b8' }}
                          tickFormatter={(v) => v.slice(5)}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis 
                          tick={{ fontSize: 10, fill: '#94a3b8' }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            borderRadius: 12, 
                            border: 'none', 
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                            fontSize: 12
                          }}
                        />
                        <Bar dataKey="views" fill={CHART_COLORS.primary} radius={[4, 4, 0, 0]} name="Views" />
                        <Bar dataKey="interactions" fill={CHART_COLORS.secondary} radius={[4, 4, 0, 0]} name="Interactions" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex justify-center gap-6 mt-4 pt-4 border-t border-slate-100">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-teal-500" />
                      <span className="text-xs text-slate-600">Views</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-sky-500" />
                      <span className="text-xs text-slate-600">Interactions</span>
                    </div>
                  </div>
                </div>
              </section>

              {/* Performance Breakdown */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Listings by Views */}
                <section>
                  <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
                    Top Performing Listings
                  </h2>
                  <div className="bg-white rounded-2xl p-5 shadow-sm">
                    {data.top_listings.length > 0 ? (
                      data.top_listings.map((listing, idx) => (
                        <TopListingRow 
                          key={listing.business_id}
                          rank={idx + 1}
                          name={listing.name}
                          category={listing.category}
                          views={listing.views}
                          interactions={listing.interactions}
                          isFeatured={listing.is_featured}
                        />
                      ))
                    ) : (
                      <p className="text-sm text-slate-400 text-center py-6">No data yet</p>
                    )}
                  </div>
                </section>

                {/* Top Categories */}
                <section>
                  <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
                    Top Categories
                  </h2>
                  <div className="bg-white rounded-2xl p-5 shadow-sm">
                    {data.top_categories.length > 0 ? (
                      data.top_categories.map((cat) => (
                        <CategoryBar 
                          key={cat.category}
                          category={cat.category}
                          engagement={cat.engagement}
                          maxEngagement={maxCategoryEngagement}
                        />
                      ))
                    ) : (
                      <p className="text-sm text-slate-400 text-center py-6">No data yet</p>
                    )}
                  </div>
                </section>
              </div>

              {/* Device & Location Insights */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Device Breakdown */}
                <section>
                  <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
                    Device Breakdown
                  </h2>
                  <div className="bg-white rounded-2xl p-5 shadow-sm">
                    <div className="flex items-center justify-around">
                      <div className="text-center">
                        <div className="w-16 h-16 rounded-2xl bg-teal-50 flex items-center justify-center mx-auto mb-2">
                          <Smartphone className="w-8 h-8 text-teal-600" />
                        </div>
                        <p className="text-2xl font-bold text-slate-900">{data.device_breakdown.mobile}%</p>
                        <p className="text-xs text-slate-500">Mobile</p>
                      </div>
                      <div className="w-px h-16 bg-slate-100" />
                      <div className="text-center">
                        <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto mb-2">
                          <Monitor className="w-8 h-8 text-slate-400" />
                        </div>
                        <p className="text-2xl font-bold text-slate-900">{data.device_breakdown.desktop}%</p>
                        <p className="text-xs text-slate-500">Desktop</p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Time of Day */}
                <section>
                  <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
                    Usage by Hour
                  </h2>
                  <div className="bg-white rounded-2xl p-5 shadow-sm">
                    <div className="h-32">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data.time_patterns}>
                          <XAxis 
                            dataKey="hour" 
                            tick={{ fontSize: 8, fill: '#94a3b8' }}
                            axisLine={false}
                            tickLine={false}
                            interval={2}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              borderRadius: 8, 
                              border: 'none', 
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                              fontSize: 11
                            }}
                            formatter={(value) => [value, 'Events']}
                            labelFormatter={(label) => `${label}:00`}
                          />
                          <Bar dataKey="count" fill={CHART_COLORS.tertiary} radius={[2, 2, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </section>
              </div>

              {/* QR Source Breakdown (if data exists) */}
              {data.qr_sources && data.qr_sources.length > 0 && (
                <section>
                  <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
                    QR Scan Sources
                  </h2>
                  <div className="bg-white rounded-2xl p-5 shadow-sm">
                    <div className="space-y-2">
                      {data.qr_sources.map((source) => (
                        <div key={source.source || 'direct'} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                          <span className="text-sm text-slate-700">{source.source || 'Direct'}</span>
                          <span className="text-sm font-semibold text-slate-900">{source.scans.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              )}

              {/* Export Note */}
              <div className="text-center py-4">
                <p className="text-xs text-slate-400">
                  Analytics data is updated in real-time. Use this data to demonstrate platform value to partners.
                </p>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
